import { useState, useEffect } from 'react';
import { notesApi, Note } from '../../api/notes.api';
import Table, { Column } from '../../components/common/Table';
import Button from '../../components/forms/Button';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';
import Input from '../../components/forms/Input';

const NoteList = () => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
  });

  useEffect(() => {
    fetchNotes();
  }, []);

  const fetchNotes = async () => {
    try {
      setIsLoading(true);
      const data = await notesApi.getAll();
      setNotes(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Notlar yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (note?: Note) => {
    if (note) {
      setEditingNote(note);
      setFormData({
        title: note.title,
        content: note.content,
      });
    } else {
      setEditingNote(null);
      setFormData({
        title: '',
        content: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingNote(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingNote) {
        await notesApi.update(editingNote.id, formData);
      } else {
        await notesApi.create(formData);
      }
      await fetchNotes();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'İşlem başarısız oldu');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu notu silmek istediğinizden emin misiniz?')) {
      return;
    }
    try {
      await notesApi.delete(id);
      await fetchNotes();
    } catch (err: any) {
      setError(err.message || 'Silme işlemi başarısız oldu');
    }
  };

  const handleToggleComplete = async (note: Note) => {
    try {
      await notesApi.update(note.id, { isCompleted: !note.isCompleted });
      await fetchNotes();
    } catch (err: any) {
      setError(err.message || 'Güncelleme başarısız oldu');
    }
  };

  const columns: Column<Note>[] = [
    { key: 'title', header: 'Başlık' },
    { key: 'content', header: 'İçerik' },
    {
      key: 'isCompleted',
      header: 'Durum',
      render: (item) => (
        <button
          onClick={() => handleToggleComplete(item)}
          className={`px-2 py-1 rounded-full text-xs font-semibold ${
            item.isCompleted ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
          }`}
        >
          {item.isCompleted ? 'Tamamlandı' : 'Beklemede'}
        </button>
      ),
    },
    {
      key: 'actions',
      header: 'İşlemler',
      render: (item) => (
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => handleOpenModal(item)}>
            Düzenle
          </Button>
          <Button size="sm" variant="danger" onClick={() => handleDelete(item.id)}>
            Sil
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <Loading text="Notlar yükleniyor..." />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Notlarım</h1>
        <Button onClick={() => handleOpenModal()}>Yeni Not Ekle</Button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <Table data={notes} columns={columns} emptyMessage="Henüz not bulunmamaktadır" />

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingNote ? 'Not Düzenle' : 'Yeni Not Ekle'}
      >
        <form onSubmit={handleSubmit}>
          <Input
            label="Başlık"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
          <Input
            label="İçerik"
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            required
          />
          <div className="flex gap-2 mt-6">
            <Button type="submit" fullWidth>
              {editingNote ? 'Güncelle' : 'Ekle'}
            </Button>
            <Button type="button" variant="outline" fullWidth onClick={handleCloseModal}>
              İptal
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default NoteList;
